import { useState, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';

const SUBMIT_URL = 'https://readdy.ai/api/form/dae3prrep053ijub1870';

const tipTypes = [
  '消费维权', '食品安全', '物业纠纷', '网络欺诈',
  '交通安全', '公共环境', '社会民生', '行业黑料', '其他',
];

const notices = [
  '请确保提供的线索真实可查，避免恶意捏造或诽谤他人。',
  '17爆料对所有投稿人信息严格保密，支持完全匿名投稿。',
  '线索经编辑部核实后方可发布，不保证所有线索均被采用。',
  '对于涉及个人隐私的信息，我们会在发布前进行必要处理。',
  '如需跟进投稿状态，请留下有效联系方式，否则无法通知。',
];

interface FormState {
  tipType: string;
  title: string;
  content: string;
  contact: string;
  isAnonymous: boolean;
  website_alt: string;
}

export default function SubmitPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formState, setFormState] = useState<FormState>({
    tipType: '',
    title: '',
    content: '',
    contact: '',
    isAnonymous: false,
    website_alt: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const contentMaxLen = 500;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormState((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormState((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Honeypot check
    const honeypotVal = formRef.current?.querySelector<HTMLInputElement>('input[name="website_alt"]')?.value?.trim();
    if (honeypotVal) {
      setStatus('success');
      return;
    }

    if (!formState.tipType || !formState.title || !formState.content) {
      setErrorMsg('请填写线索类型、标题和内容后再提交。');
      setStatus('error');
      return;
    }
    if (formState.content.length > contentMaxLen) {
      setErrorMsg(`线索详情不能超过${contentMaxLen}字。`);
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const formData = new FormData(formRef.current!);
      const payload = new URLSearchParams();
      formData.forEach((val, key) => {
        if (key !== 'website_alt') {
          payload.append(key, val.toString());
        }
      });

      const resp = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await resp.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch (_) { /* raw text */ }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '';

      if (resp.ok && parsed?.code === 'OK') {
        setStatus('success');
        formRef.current?.reset();
        setFormState({ tipType: '', title: '', content: '', contact: '', isAnonymous: false, website_alt: '' });
      } else if (serverMsg.includes('spam')) {
        setStatus('error');
        setErrorMsg('提交失败：内容被识别为可疑信息，请修改后重试。');
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || '提交失败，请稍后重试。');
      }
    } catch (_) {
      setStatus('error');
      setErrorMsg('网络异常，请检查网络后重试。');
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xodxd.cn/' },
      { '@type': 'ListItem', position: 2, name: '17爆料投稿', item: 'https://xodxd.cn/submit' },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-16">
        {/* Page hero */}
        <div className="relative bg-foreground-950 py-12 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20textured%20paper%20background%20with%20subtle%20grain%20and%20warm%20amber%20ink%20patterns%2C%20editorial%20minimalism%2C%20deep%20charcoal%20and%20rust%20tones%2C%20high%20contrast%20artistic%20texture&width=1600&height=400&seq=submit-hero-bg-01&orientation=landscape"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-20"
          />
          <div className="relative max-w-6xl mx-auto px-4 md:px-6">
            <nav aria-label="面包屑" className="text-xs text-background-400 mb-4">
              <ol className="flex items-center gap-1.5 flex-wrap">
                <li><a href="/" className="hover:text-background-200 transition-colors">首页</a></li>
                <li><i className="ri-arrow-right-s-line" /></li>
                <li className="text-background-200">17爆料投稿</li>
              </ol>
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-background-50 mb-3">
              17爆料投稿 / 私信
            </h1>
            <p className="text-background-400 text-sm md:text-base max-w-xl">
              在这里提交你的线索。编辑部收到后会进行多方核实，通过核实的内容将在相关栏目公开发布。
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Form */}
          <section className="lg:col-span-2">
            <h2 className="headline-divider font-heading text-xl font-bold text-foreground-950 mb-6">在线投稿表单</h2>

            {status === 'success' ? (
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-3xl" />
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">线索已收到！</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                  感谢你通过17爆料投稿提供线索。编辑部将在1–3个工作日内完成初步审核，如需进一步核实我们会主动联系你。
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="px-5 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  继续提交线索
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                id="submit-tip-form"
                data-readdy-form
                onSubmit={handleSubmit}
                noValidate
                className="flex flex-col gap-5"
              >
                {/* Honeypot */}
                <div className="form-field-alt-hide" aria-hidden="true">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    value={formState.website_alt}
                    onChange={handleChange}
                  />
                </div>

                {/* Tip type */}
                <div>
                  <label htmlFor="tipType" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    线索类型 <span className="text-primary-500">*</span>
                  </label>
                  <select
                    id="tipType"
                    name="tipType"
                    required
                    value={formState.tipType}
                    onChange={handleChange}
                    className="w-full text-sm border border-background-300 rounded-md px-3 py-2.5 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
                  >
                    <option value="">请选择线索类型</option>
                    {tipTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    线索标题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="title"
                    type="text"
                    name="title"
                    required
                    placeholder="用一句话概括你的爆料（20字以内）"
                    value={formState.title}
                    onChange={handleChange}
                    className="w-full text-sm border border-background-300 rounded-md px-3 py-2.5 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                {/* Content */}
                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    线索详情 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="content"
                    name="content"
                    required
                    rows={7}
                    placeholder="请详细描述事情经过，包括时间、地点、当事方，以及你掌握的主要证据或线索。内容越详细，核实效率越高。（最多500字）"
                    value={formState.content}
                    onChange={handleChange}
                    maxLength={contentMaxLen}
                    className="w-full text-sm border border-background-300 rounded-md px-3 py-2.5 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">
                    {formState.content.length}/{contentMaxLen} 字
                  </p>
                </div>

                {/* Contact */}
                <div>
                  <label htmlFor="contact" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    联系方式（选填）
                  </label>
                  <input
                    id="contact"
                    type="text"
                    name="contact"
                    placeholder="微信号 / 手机 / 邮箱，留下后编辑部可主动联系你"
                    value={formState.contact}
                    onChange={handleChange}
                    className="w-full text-sm border border-background-300 rounded-md px-3 py-2.5 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                {/* Anonymous */}
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="isAnonymous"
                    checked={formState.isAnonymous}
                    onChange={handleChange}
                    className="w-4 h-4 accent-primary-500 cursor-pointer"
                  />
                  <span className="text-sm text-foreground-700">我希望以匿名方式提交，不公开任何个人信息</span>
                </label>

                {/* Error */}
                {status === 'error' && (
                  <div className="bg-primary-50 border border-primary-200 rounded-md px-4 py-3 text-sm text-primary-700">
                    <i className="ri-error-warning-line mr-2" />
                    {errorMsg}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 font-semibold rounded-md transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2" />
                      正在提交...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill mr-2" />
                      提交线索
                    </>
                  )}
                </button>
              </form>
            )}
          </section>

          {/* Sidebar */}
          <aside className="flex flex-col gap-6">
            {/* Notice */}
            <div className="bg-background-100 rounded-lg p-5 border border-background-200">
              <h3 className="font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-information-line" />
                </span>
                投稿须知
              </h3>
              <ul className="flex flex-col gap-3">
                {notices.map((n, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-foreground-600 leading-relaxed">
                    <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-[10px] font-bold mt-0.5">
                      {i + 1}
                    </span>
                    {n}
                  </li>
                ))}
              </ul>
            </div>

            {/* Email channel */}
            <div className="bg-foreground-950 rounded-lg p-5 text-background-200">
              <h3 className="font-heading font-bold text-background-50 mb-3">
                <span className="w-5 h-5 inline-flex items-center justify-center text-primary-400 mr-1">
                  <i className="ri-mail-send-line" />
                </span>
                邮件私信通道
              </h3>
              <p className="text-xs leading-relaxed mb-3">
                如你有特殊情况或希望附上大容量文件，可直接通过17爆料私信邮箱联系我们。编辑部同样保证对邮件信源的严格保密。
              </p>
              <a
                href="mailto:tips@17baoliao.com"
                rel="nofollow"
                className="inline-flex items-center gap-1.5 text-sm text-primary-400 hover:text-primary-300 font-medium transition-colors"
              >
                <i className="ri-mail-line" />
                tips@17baoliao.com
              </a>
            </div>

            {/* Tips */}
            <div className="bg-accent-50 border border-accent-200 rounded-lg p-5">
              <h3 className="font-heading font-bold text-foreground-900 mb-3">
                <span className="w-5 h-5 inline-flex items-center justify-center text-accent-600 mr-1">
                  <i className="ri-thumb-up-line" />
                </span>
                让线索更容易被核实
              </h3>
              <ul className="flex flex-col gap-2 text-xs text-foreground-600 leading-relaxed">
                <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" />注明具体时间、地点和当事方名称</li>
                <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" />提供聊天截图、收据、照片等佐证</li>
                <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" />描述你与事件的关系（目击者/当事人/知情人）</li>
                <li className="flex items-start gap-1.5"><i className="ri-checkbox-circle-line text-accent-500 mt-0.5" />如有其他当事人可联系，请注明</li>
              </ul>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}